import getLastComment from '@opsbase/lib-conversation/helpers/getLastComment';
import i18n from '@opsbase/lib-i18n';
import Flag from '@opsbase/lib-issue/Flag';
import issueIs from '@opsbase/lib-issue/helpers/issueIs';
import { issueStatusColor, issueStatusLabel } from '@opsbase/lib-issue/IssueStatus';
import Pages from '@opsbase/lib-pages';
import Palette from '@opsbase/lib-palette';
import SPTType from '@opsbase/lib-spt';
import { DateTimeFormat } from '@opsbase/lib-time/consts';
import { humanizeFromNow, logStamp } from '@opsbase/lib-time/utils';
import useSelf from '@opsbase/react-ctrl-self/useSelf';
import IssueIcon from '@opsbase/web-icons/icons/IssueIcon';
import useNav from '@opsbase/web-nav/useNav';
import React, { useCallback, useMemo } from 'react';
import ToDoCard from './ToDoCard';
export function IssueToDoCard(props) {
    const { item: issue } = props;
    const { goTo } = useNav();
    const self = useSelf();
    const handleClick = useCallback(() => {
        return goTo(Pages.ISSUES_VIEW, { id: issue.id });
    }, [goTo, issue]);
    const cardProps = useMemo(() => {
        var _a, _b, _c;
        return ({
            id: issue.id,
            title: issue.title,
            description: (_a = getLastComment(issue === null || issue === void 0 ? void 0 : issue.conversation)) === null || _a === void 0 ? void 0 : _a.title,
            watermark: IssueIcon,
            statusLabel: calcStatusLabel(issue),
            statusColor: calcStatusColor(issue),
            assignedTo: calcAssignedTo(issue),
            watermarkColor: calcWatermarkColor(issue),
            place: (_b = issue.place) === null || _b === void 0 ? void 0 : _b.title,
            asset: (_c = issue.asset) === null || _c === void 0 ? void 0 : _c.title,
            tags: issue.type ? [self.settings.getIssueTypeTitle(issue.type)] : [],
            dueLabel: calcDueLabel(issue),
            dueTip: calcDueTip(issue),
            dueColor: calcDueColor(issue),
            updatedLabel: calcUpdatedLabel(issue),
            updatedTip: calcUpdatedTip(issue),
            isLate: issueIs.late(issue),
            isClosed: issueIs.closed(issue),
            isHighlighted: calcIsHighlighted(issue, self),
        });
    }, [issue, self]);
    return React.createElement(ToDoCard, { ...cardProps, onClick: handleClick });
}
export default IssueToDoCard;
function calcIsHighlighted(issue, self) {
    var _a;
    return ((_a = issue.assignedToStaff) === null || _a === void 0 ? void 0 : _a.id) === self.id;
}
function calcStatusLabel(issue) {
    return issueStatusLabel(issue.status);
}
function calcStatusColor(issue) {
    return issueStatusColor(issue.status);
}
function calcDueLabel(issue) {
    if (!issue.deadlineDate)
        return undefined;
    return 'Due ' + humanizeFromNow(issue.deadlineDate);
}
function calcDueTip(issue) {
    if (!issue.deadlineDate)
        return undefined;
    return issue.deadlineDate.format(DateTimeFormat);
}
function calcDueColor(issue) {
    if (!issueIs.closed(issue) && issueIs.late(issue))
        return Palette.OJ;
    return Palette.BlackCoral;
}
function calcUpdatedLabel(issue) {
    // Closed Ops
    if (!!issue.closedDate) {
        return logStamp(issue.closedDate, i18n.t('closed'));
    }
    // Open Ops
    if (!!issue.startedDate && issue.startedDate.isSame(issue.updatedDate))
        return logStamp(issue.startedDate, i18n.t('started'));
    if (!!issue.updatedDate)
        return logStamp(issue.updatedDate, i18n.t('updated'));
    return logStamp(issue.occurrenceDate, i18n.t('created'));
}
function calcUpdatedTip(issue) {
    var _a, _b, _c, _d;
    // Closed Ops
    if (!!issue.closedDate)
        return (_a = issue.closedBy) === null || _a === void 0 ? void 0 : _a.title;
    // Open Ops
    if (!!issue.startedDate && issue.startedDate.isSame(issue.updatedDate))
        return (_b = issue.startedBy) === null || _b === void 0 ? void 0 : _b.title;
    if (!!issue.updatedDate)
        return (_c = issue.updatedBy) === null || _c === void 0 ? void 0 : _c.title;
    return (_d = issue.createdBy) === null || _d === void 0 ? void 0 : _d.title;
}
function calcAssignedTo(issue) {
    return {
        staff: issue.assignedToSPTType === SPTType.STAFF && !!issue.assignedToStaff
            ? [issue.assignedToStaff]
            : [],
        positions: issue.assignedToSPTType === SPTType.POSITION && !!issue.assignedToPosition
            ? [issue.assignedToPosition]
            : [],
        teams: issue.assignedToSPTType === SPTType.TEAM && !!issue.assignedToTeam
            ? [issue.assignedToTeam]
            : [],
    };
}
function calcWatermarkColor(issue) {
    switch (issue.flag) {
        case Flag.GREEN:
            return Palette.Greeny;
        case Flag.ORANGE:
            return Palette.OJ;
        case Flag.RED:
            return Palette.CoralRed;
        case Flag.BLACK:
            return Palette.GreyBlue;
        case Flag.PURPLE:
            return Palette.LibertyPurple;
        case Flag.BLUE:
            return Palette.GoodOldBlue;
        default:
            return Palette.GreyBlue;
    }
}
